import * as React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import {
  SIDEBAR_COLOR_MODE,
  setStoredSidebarDarkMode,
  useSidebarDarkMode,
} from "../../useAppLayoutAppearance";
import SideMenuMobile from "./SideMenuMobile";
import { APP_SHELL, getPrivateShellColors } from "./MenuContent";

const SHOW_GLOBAL_SEARCH = false;
const SHOW_SHELL_NOTIFICATIONS = false;

function openDocumentation() {
  window.open("https://123zap.gitbook.io/docs", "_blank", "noopener,noreferrer");
}

function TopbarAction({ children, colors, label, onClick, wide = false, highlighted = false }) {
  return (
    <Tooltip title={label}>
      <ButtonBase
        type="button"
        aria-label={label}
        onClick={onClick}
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          color: highlighted ? colors.activeText : colors.muted,
          display: "flex",
          height: 36,
          justifyContent: "center",
          minWidth: wide ? 72 : 40,
          px: wide ? 1.5 : 0,
          "&:hover": {
            bgcolor: highlighted ? colors.activeBg : theme.palette.action.hover,
            borderColor: highlighted ? colors.activeText : colors.border,
            color: highlighted ? colors.activeText : colors.text,
          },
        })}
      >
        {children}
      </ButtonBase>
    </Tooltip>
  );
}

function DesktopTopbar({ sidebarDark }) {
  const theme = useTheme();
  const colors = getPrivateShellColors(theme, sidebarDark);

  return (
    <Box
      component="header"
      sx={{
        "--topbar-muted": colors.muted,
        alignItems: "center",
        bgcolor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        display: { xs: "none", md: "flex" },
        flexShrink: 0,
        height: APP_SHELL.topbarHeight,
        px: 3,
        width: "100%",
      }}
    >
      {SHOW_GLOBAL_SEARCH ? (
        <Box
          role="search"
          sx={{
            alignItems: "center",
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            display: "flex",
            height: 40,
            maxWidth: 336,
            px: 1.75,
            width: "min(336px, 36vw)",
          }}
        >
          <SearchRoundedIcon sx={{ color: "var(--topbar-muted)", fontSize: 18 }} />
          <InputBase
            disabled
            placeholder="Buscar"
            inputProps={{ "aria-label": "Buscar" }}
            sx={{
              color: colors.muted,
              fontSize: 13,
              ml: 1.25,
              width: "100%",
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: colors.muted,
              },
            }}
          />
        </Box>
      ) : null}

      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", ml: "auto" }}>
        <TopbarAction
          colors={colors}
          label="Guias"
          onClick={openDocumentation}
          wide
        >
          <Typography
            component="span"
            sx={{
              color: "currentColor",
              fontSize: 13,
              fontWeight: 600,
              lineHeight: "18px",
            }}
          >
            Guias
          </Typography>
        </TopbarAction>
        <TopbarAction
          colors={colors}
          label={sidebarDark ? "Usar tema claro" : "Usar tema escuro"}
          onClick={() => {
            if (!SIDEBAR_COLOR_MODE.toggleEnabled) return;
            setStoredSidebarDarkMode(!sidebarDark);
          }}
        >
          {sidebarDark ? (
            <LightModeOutlinedIcon sx={{ fontSize: 18 }} />
          ) : (
            <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />
          )}
        </TopbarAction>
        {SHOW_SHELL_NOTIFICATIONS ? (
          <TopbarAction colors={colors} label="Notificações" highlighted>
            <NotificationsNoneRoundedIcon sx={{ fontSize: 18 }} />
          </TopbarAction>
        ) : null}
      </Stack>
    </Box>
  );
}

function MobileTopbar({ onOpen, sidebarDark }) {
  const theme = useTheme();
  const colors = getPrivateShellColors(theme, sidebarDark);

  return (
    <Box
      component="header"
      sx={(theme) => ({
        alignItems: "center",
        bgcolor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        display: { xs: "flex", md: "none" },
        height: APP_SHELL.mobileHeaderHeight,
        left: 0,
        px: 2,
        position: "fixed",
        right: 0,
        top: "var(--template-frame-height, 0px)",
        zIndex: theme.zIndex.appBar,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            color: colors.brand,
            fontSize: 17,
            fontWeight: 800,
            lineHeight: "25px",
          }}
        >
          123zap
        </Typography>
        <Box
          aria-hidden="true"
          sx={{
            bgcolor: colors.muted,
            borderRadius: 1,
            height: 8,
            width: 104,
          }}
        />
      </Box>

      <ButtonBase
        type="button"
        aria-label="Abrir menu"
        onClick={onOpen}
        sx={{
          alignItems: "center",
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          color: colors.muted,
          display: "flex",
          height: 36,
          justifyContent: "center",
          ml: "auto",
          width: 40,
        }}
      >
        <MenuRoundedIcon sx={{ fontSize: 20 }} />
      </ButtonBase>
    </Box>
  );
}

export default function AppNavbar() {
  const [open, setOpen] = React.useState(false);
  const sidebarDark = useSidebarDarkMode();

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  return (
    <>
      <DesktopTopbar sidebarDark={sidebarDark} />
      <MobileTopbar onOpen={toggleDrawer(true)} sidebarDark={sidebarDark} />
      <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
    </>
  );
}
